import {useState, useEffect, useRef} from 'react'
import {CircularProgress, Divider, FormGroup, Grid, Stack} from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import ListAltIcon from '@mui/icons-material/ListAlt'
import Draggable from 'react-draggable'
import {
  CustomLinearProgress,
  CustomBox,
  StyledButtonDiv,
  StyledTextPercentage,
  CustomCheckbox,
  CustomFormControlLabel,
  StyledDivWarning,
  StyledWarningText,
  StyledSummaryDiv,
  StyledSummaryText,
  StyledPersonaText,
  StyledPersonaTitle,
  StyledHandleDragDiv,
  StyledMainDiv,
  StyledDragIndicatorIcon,
  StyledKeyboardArrowDownIcon,
  StyledKeyboardArrowUpIcon,
  StyledCheckboxText,
  classesP
} from './PersonasStyle'
import {usePersona} from '../../../store/hooks/persona'
import {useConfig} from '../../../store/hooks/config'
import {usePersonaQuery} from '../../../store/hooks/personaQuery'
import {initialData} from './InitialPersonaData'
import {AnalyticsHandler} from '../../../shared/handlers/analytics/handlers'
import {usePersonaData} from '../../../store/hooks/personaMapData'
import {getPersonaCustomName} from '../helper'

const Personas = () => {
  const {
    personaSidebarOpen,
    setPersonaSidebarOpen,
    setPersonas,
    personasToggle,
    setPersonasToggle,
    selectedPersonas: rawSelectedPersonas,
    setSelectedPersonas
  } = usePersona()

  // Ensure selectedPersonas is always an array
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedPersonas = Array.isArray(rawSelectedPersonas) ? rawSelectedPersonas : []

  const {setPersonaPercentage} = usePersonaQuery()
  const [personaList, setPersonaList] = useState(initialData)
  const {personaPercentage, loading} = usePersonaData()
  const [showAll, setShowAll] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const {personasConfig} = useConfig()
  const personaListRef = useRef(null)

  // Avoid mutating state directly in useEffect
  const updatePersonaList = () => {
    const tempList = structuredClone(initialData)

    for (const item of tempList) {
      if (selectedPersonas.includes(item.persona.trim())) {
        item.percentage = -1
        item.personaCount = personaPercentage?.length ? 0 : -1
      } else {
        item.percentage = -1
        item.personaCount = -1
      }
    }

    if (personaPercentage?.length === 0) {
      personaPercentage.personaPercentage = structuredClone(initialData)
    }

    for (const item of personaPercentage?.personaPercentage || []) {
      const idx = tempList.findIndex((ele) => ele.persona.includes(item.persona.trim()))
      if (idx !== -1) tempList[idx] = item
    }

    tempList.sort((a, b) => b.personaCount - a.personaCount)
    setPersonaList(tempList)
    setPersonaPercentage(tempList)

    if (selectedPersonas.includes(personaSidebarOpen.name.trim())) {
      setPersonaSidebarOpen({
        ...personaSidebarOpen,
        value: tempList.findIndex((ele) => ele.persona.includes(personaSidebarOpen.name.trim()))
      })
    } else {
      setPersonaSidebarOpen({
        ...personaSidebarOpen,
        name: tempList[0]?.persona.trim(),
        value: 0
      })
    }
  }

  useEffect(() => {
    if (!loading) {
      updatePersonaList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, personaPercentage]) // Removed unnecessary dependencies

  // Re-fetching the persona percentage based on the selected filters
  useEffect(() => {
    setPersonas(selectedPersonas)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPersonas, setPersonas])

  useEffect(() => {
    if (personasToggle) {
      personaListRef?.current?.focus()
    }
  }, [personasToggle])

  useEffect(() => {
    if (showAll) {
      const element = document.getElementById('personaHover5')
      element?.focus()
    }
  }, [showAll])

  const handleCheckboxChange = (event) => {
    const {value, id, checked} = event.target

    const ele = document.getElementById(`persona-${value}`)

    if (checked) {
      // if element is checked i.e selected
      ele?.classList?.remove(classesP.disable)
      setSelectedPersonas([...selectedPersonas, value])
      setShowWarning(false)
    } else if (selectedPersonas.length === 1) {
      // if only one element is selected
      setShowWarning(true)
    } else {
      // when element is unchecked i.e deselected we disable it
      ele?.classList?.add(classesP.disable)
      document.getElementById(id)?.classList?.remove(classesP.background)
      setSelectedPersonas(selectedPersonas.filter((key) => key !== value))
    }
  }

  // This function will handle the click event on the persona name in the sidebar i.e. for individual persona (overview)
  const handleIndividualPersonaChange = (personaName, index) => {
    const summary = document?.getElementById('summary')
    const prevEle = document?.getElementById('personaHover' + personaSidebarOpen.value)
    const currentEle = document?.getElementById('personaHover' + index)
    const currentEleDisable = document?.getElementById(`persona-${personaName}`)

    summary?.classList?.remove(classesP.background)

    if (currentEleDisable?.classList?.contains(classesP.disable)) {
      return null
    } else {
      AnalyticsHandler.onPersonaSelect({
        name: personaName,
        section: 'Overview'
      })
      prevEle?.classList?.remove(classesP.background)
      currentEle?.classList?.add(classesP.background)
      setPersonaSidebarOpen({
        component: 'individualPersona',
        name: personaName,
        value: index,
        open: true
      })
    }
  }

  // This function will handle the click event on the summary in the sidebar i.e. for all personas
  const handleSummaryPersonaChange = () => {
    const prevEle = document?.getElementById('personaHover' + personaSidebarOpen.value)
    const summary = document?.getElementById('summary')
    prevEle?.classList?.remove(classesP.background)

    summary?.classList?.add(classesP.background)

    setPersonaSidebarOpen({
      component: 'summary',
      name: 'Personas',
      value: 0,
      open: personaSidebarOpen.component === 'summary' ? !personaSidebarOpen.open : true // checking which component is active for closing mechanism
    })

    if (!personaSidebarOpen.open || personaSidebarOpen.component !== 'summary')
      AnalyticsHandler.onPersonaSelect({
        name: 'Personas',
        section: 'Summary'
      })

    if (personaSidebarOpen.open && personaSidebarOpen.component === 'summary')
      summary?.classList?.remove(classesP.background)
  }

  // This function will return the value of the progress bar based on the selected persona
  const getValue = (item, component) => {
    if (selectedPersonas.includes(item.persona.trim())) {
      if (item.percentage >= 1) {
        return item.percentage
      } else if (item.percentage === -1) {
        if (component === 'percentage') {
          return 0
        }
        return -1
      } else {
        return component === 'percentage' ? '<1' : 1
      }
    }
    return -1
  }

  const personasText =
    'There are 15 distinct Persona Archetypes that exist within the United States. These Personas are displayed based on your selected geography and population.'
  const warningText = 'At least one persona must be selected.'
  const summaryText = `View Summary for Selected Personas (${selectedPersonas?.length})`
  const visiblePersonas = showAll ? personaList : personaList.slice(0, 5)
  const percentageText = 'Percentages are rounded off to the nearest whole number.'

  // This function will handle the collapse and expand of the personas sidebar
  const handleCollapseToggle = () => {
    setIsCollapsed(!isCollapsed)
  }

  const handleKeyDown = (event, action) => {
    if ((event.key === 'Enter' || event.key === ' ') && action) {
      event.preventDefault()
      action()
    }
    if (event.key === 'Escape' && !action) {
      event.preventDefault()
      setPersonasToggle(false)
      document.getElementById('persona-toggle-switch')?.focus()
    }
  }

  return (
    personasToggle && (
      <Draggable bounds='parent' handle='.drag-handle'>
        <CustomBox
          personaSidebar={personaSidebarOpen.open}
          ref={personaListRef}
          onKeyDown={(event) => handleKeyDown(event, null)}
          tabIndex={0}
          aria-label='Persona List Panel'
          id='persona-list-panel'
        >
          <StyledMainDiv>
            <StyledHandleDragDiv
              className='drag-handle'
              tabIndex={0}
              onKeyDown={(event) => handleKeyDown(event, handleCollapseToggle)}
              aria-label={isCollapsed ? 'Expand Persona List' : 'Collapse Persona List'}
            >
              {isCollapsed ? (
                <StyledKeyboardArrowDownIcon onClick={handleCollapseToggle} />
              ) : (
                <StyledKeyboardArrowUpIcon onClick={handleCollapseToggle} />
              )}
              <StyledPersonaTitle>Personas</StyledPersonaTitle>
            </StyledHandleDragDiv>
            <StyledDragIndicatorIcon className='drag-handle' />
          </StyledMainDiv>
          {!isCollapsed && (
            <>
              <Divider />
              <StyledPersonaText id='body2' tabIndex={0}>
                {personasText}
              </StyledPersonaText>
              <StyledSummaryDiv id='summary'>
                <ListAltIcon />
                <StyledSummaryText
                  onClick={handleSummaryPersonaChange}
                  tabIndex={0}
                  aria-label={summaryText}
                  onKeyDown={(event) => handleKeyDown(event, handleSummaryPersonaChange)}
                >
                  {summaryText}
                </StyledSummaryText>
              </StyledSummaryDiv>
              <Divider />
              <StyledPersonaText id='labelSmall' className={classesP.textFS} tabIndex={0}>
                {percentageText}
              </StyledPersonaText>
              {/* Loading state here */}
              {loading && (
                <Stack className={classesP.stackFS} spacing={1} direction='row'>
                  <StyledPersonaText id='subtitle3'>Reordering list...</StyledPersonaText>
                  <CircularProgress size={20} title='loading' />
                </Stack>
              )}

              {showWarning && (
                <StyledDivWarning tabIndex={0} aria-label='Warning: At least one persona must be selected'>
                  <WarningIcon />
                  <StyledWarningText>{warningText}</StyledWarningText>
                </StyledDivWarning>
              )}
              <FormGroup>
                {visiblePersonas.map((item, index) => (
                  <Grid
                    container
                    item
                    key={index + item.persona.trim()}
                    id={'personaHover' + index}
                    className={classesP.hoverBackground}
                    tabIndex={0}
                    aria-label={`Persona ${item.persona.trim()}, ${getValue(item, 'percentage')}%`}
                  >
                    <Grid xs={2} item>
                      <CustomFormControlLabel
                        control={
                          <CustomCheckbox
                            value={item.persona.trim()}
                            data-testid={item.persona.trim()}
                            id={item.persona.trim()}
                            checked={selectedPersonas.includes(item.persona.trim())}
                            onChange={handleCheckboxChange}
                            tabIndex={0}
                            aria-label={`Select or deselect persona ${item.persona.trim()}`}
                          />
                        }
                      />
                    </Grid>
                    <Grid xs={6} item>
                      <StyledCheckboxText
                        id={`persona-${item.persona.trim()}`}
                        className={selectedPersonas.includes(item.persona.trim()) ? '' : classesP.disable}
                        onClick={() => handleIndividualPersonaChange(item.persona.trim(), index)}
                        onKeyDown={(event) =>
                          handleKeyDown(event, () => handleIndividualPersonaChange(item.persona.trim(), index))
                        }
                        tabIndex={0}
                        aria-label={`View details for persona ${item.persona.trim()}`}
                      >
                        {getPersonaCustomName(item.persona.trim(), personasConfig)}
                      </StyledCheckboxText>
                    </Grid>
                    <Grid xs={3} item>
                      <CustomLinearProgress
                        variant='determinate'
                        value={getValue(item, 'progress')}
                        id={personasConfig?.personas?.[item?.persona]}
                      />
                    </Grid>
                    <Grid xs={1} item>
                      <StyledTextPercentage>
                        {selectedPersonas.includes(item.persona.trim()) ? getValue(item, 'percentage') : 0}%
                      </StyledTextPercentage>
                    </Grid>
                  </Grid>
                ))}
              </FormGroup>
              <StyledButtonDiv
                onClick={() => setShowAll(!showAll)}
                onKeyDown={(event) => handleKeyDown(event, () => setShowAll(!showAll))}
                tabIndex={0}
                className={classesP.colorHpPrimary}
                aria-label={showAll ? 'Show less personas' : 'Show more personas'}
              >
                {showAll ? 'Show Less' : 'Show More'}
              </StyledButtonDiv>
            </>
          )}
        </CustomBox>
      </Draggable>
    )
  )
}

export default Personas

/**
  Personas Component
 
  Function Descriptions:
  - handleCheckboxChange: Toggles selection of personas and manages warning state.
  - handleIndividualPersonaChange: Handles selection of an individual persona in the sidebar.
  - handleSummaryPersonaChange: Toggles the summary view for all personas in the sidebar.
  - getValue: Returns progress bar value or percentage for a persona.
  - handleCollapseToggle: Toggles collapse/expand state of the personas sidebar.
  - handleKeyDown: Handles keyboard interactions for accessibility.
 */
